import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { BaseToastComponent } from '../../ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../ui-components/shared.module';
import { CheckoutPlan, CheckoutService } from '../../@services/front/checkout.service';
import { CheckoutProviderService } from '../../@services/front/checkout-provider.service';
import { PremiumService } from '../../@services/front/premium.service';
import { PricingService } from '../../@services/front/pricing.service';
import { PosthogService } from '../../@services/front/posthog.service';

/**
 * Le paywall.
 *
 * Deux règles tenues à la lettre :
 * - « Annulable à tout moment » est visible AVANT tout clic d'achat — c'est un
 *   argument de conversion, pas une mention légale reléguée en bas de page ;
 * - l'essai n'exige aucune carte, donc aucune reconduction surprise possible.
 */
@Component({
  selector: 'vw-premium-toast',
  imports: [SharedModule, CommonModule, TranslatePipe],
  templateUrl: './premium-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: SubscriptionApi, useClass: SubscriptionApi }],
})
export class PremiumToastComponent
  extends BaseToastComponent
  implements OnChanges
{
  /** Emplacement d'où le paywall a été ouvert (mesure du funnel). */
  @Input() source = 'unknown';

  selectedPlan: CheckoutPlan = 'YEARLY';
  loading = false;
  /** Activation en cours après retour du paiement. */
  activating = false;
  activationSlow = false;
  error = false;

  /**
   * Achat en attente d'un accord extérieur : « Demander à acheter », contrôle
   * parental, moyen de paiement différé. L'accès n'est pas immédiat, et le taire
   * laisserait croire à un échec.
   */
  purchasePending = false;

  /** Résultat de la dernière restauration : `null` = aucune tentative. */
  restored: boolean | null = null;
  restoring = false;

  private readonly authApi = new AuthApi();
  private readonly checkout = inject(CheckoutProviderService);
  private readonly stripeCheckout = inject(CheckoutService);
  private readonly posthog = inject(PosthogService);
  readonly premiumService = inject(PremiumService);
  /** Les montants affichés viennent du fournisseur, jamais des libellés. */
  readonly pricing = inject(PricingService);

  constructor(private readonly subscriptionApi: SubscriptionApi) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue && this.premiumService.premiumEnabled()) {
      this.error = false;
      this.posthog.capture('paywall_viewed', { source: this.source });
      void this.pricing.load();
      void this.premiumService.refresh().then(() => this.preselectPlan());
      this.preselectPlan();
    }
  }

  /**
   * Ouvre sur la formule DÉJÀ souscrite quand il y en a une.
   *
   * Présenter l'annuel coché à quelqu'un qui paie au mois donne l'impression
   * qu'on lui propose son abonnement actuel — puis le débite différemment.
   */
  private preselectPlan(): void {
    const current = this.premiumService.subscription()?.plan;
    if (current === 'MONTHLY' || current === 'YEARLY') {
      this.selectedPlan = current;
    }
  }

  /** L'utilisateur a déjà cette formule : rien à changer. */
  get isCurrentPlan(): boolean {
    return this.premiumService.subscription()?.plan === this.selectedPlan;
  }

  /**
   * Change-t-il de formule ? Il doit savoir à partir de quand.
   */
  get isPlanChange(): boolean {
    const current = this.premiumService.subscription()?.plan;
    return Boolean(current) && current !== this.selectedPlan;
  }

  /** Échéance de la période déjà réglée, formatée. */
  get currentPeriodEndLabel(): string {
    const end = this.premiumService.subscription()?.currentPeriodEnd;
    return end ? new Date(end).toLocaleDateString() : '';
  }

  /**
   * L'essai n'est proposé qu'une fois par compte, et jamais dans l'app native :
   * là, l'essai est celui du magasin. Débloquer l'app par notre propre mécanisme
   * y est interdit (règle 3.1.1 d'Apple).
   */
  get canStartTrial(): boolean {
    return !this.isStore && !this.premiumService.subscription()?.trialUsed;
  }

  /**
   * Conditions d'utilisation et confidentialité : Apple exige ces deux liens sur
   * l'écran d'achat d'un abonnement (règle 3.1.2).
   */
  readonly termsUrl = 'https://vegiwise.com/policies/terms-of-service';
  readonly privacyUrl = 'https://vegiwise.com/policies/privacy-policy';

  openLegal(url: string): void {
    this.stripeCheckout.openOutside(url);
  }

  /** Un invité n'a pas de compte auquel rattacher un abonnement. */
  get isGuest(): boolean {
    return this.authApi.getGroups().includes('GUEST');
  }

  /**
   * L'achat passe-t-il par le magasin ?
   *
   * Commande l'affichage de « Restaurer mes achats », qu'Apple EXIGE d'une app
   * vendant un abonnement : sans lui, quelqu'un qui réinstalle l'app ne retrouve
   * pas ce qu'il a payé, et c'est un motif de refus.
   */
  get isStore(): boolean {
    return this.checkout.isStore;
  }

  selectPlan(plan: CheckoutPlan): void {
    this.selectedPlan = plan;
  }

  async startTrial(): Promise<void> {
    this.loading = true;
    this.error = false;
    try {
      await this.subscriptionApi.startTrial();
      this.posthog.capture('trial_started', { source: this.source });
      // Le droit vient d'être créé : sans rafraîchir le jeton, l'app ne le
      // verrait qu'à sa prochaine expiration.
      await this.premiumService.refreshEntitlement();
      this.onClose({ started: true });
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  async subscribe(): Promise<void> {
    this.loading = true;
    this.error = false;
    this.purchasePending = false;
    try {
      const outcome = await this.checkout.purchase(this.selectedPlan);

      // Refermer la feuille d'achat est un geste ordinaire, pas un incident :
      // aucun message, l'écran reste tel quel.
      if (outcome === 'cancelled') return;

      if (outcome === 'pending') {
        this.purchasePending = true;
        return;
      }

      // Que le paiement se soit conclu dans le magasin (`completed`) ou hors de
      // l'app (`opened`), le DROIT vient de notre webhook — jamais du SDK du
      // prestataire. On attend donc notre propre base.
      this.activating = true;
      const activated = await this.premiumService.awaitActivation();
      this.activationSlow = !activated;
      if (activated) this.onClose({ subscribed: true });
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.activating = false;
    }
  }

  /** Restauration des achats du magasin. */
  async restorePurchases(): Promise<void> {
    this.restoring = true;
    this.error = false;
    this.restored = null;
    try {
      const found = await this.checkout.restore();
      this.restored = found;
      if (found) {
        // Le magasin a retrouvé un achat ; notre webhook doit encore ouvrir le
        // droit. On attend, comme après un achat.
        this.activating = true;
        const activated = await this.premiumService.awaitActivation();
        this.activationSlow = !activated;
        if (activated) this.onClose({ subscribed: true });
      }
    } catch {
      this.error = true;
    } finally {
      this.restoring = false;
      this.activating = false;
    }
  }
}
